/**
 * Anlagen-Checkliste — „Beleg X liegt bei?" gegen die Anlagen-Registry.
 *
 * **Der Abgleich steht seit K6 im Server** (`app/questionnaires/anlagen.py`)
 * und wird hier nur noch angezeigt. Vorher rechnete ihn der Browser ein zweites
 * Mal (`anlagenAbgleich.ts`) — mit derselben Absicht, aber in einer anderen
 * Sprache und ohne Verbindung zu der Antwort, die tatsächlich ins
 * Kundendokument geht. Zwei Fassungen derselben Regel sind die Stelle, an der
 * beide auseinanderlaufen; die Fassung, die zählt, ist die, die antwortet.
 *
 * Die eine Regel der Darstellung ist geblieben: **der gefundene Beleg ist immer
 * sichtbar.** Ein grüner Haken ohne Dokumentnamen wäre eine Behauptung, die der
 * Reviewer nicht prüfen kann — und bei den vier vertraulich gekennzeichneten
 * Belegen wäre sie gefährlich.
 */
import { useCallback, useEffect, useState } from "react";
import {
  AlertTriangle,
  ExternalLink,
  FileCheck2,
  FileQuestion,
  FileX2,
  Loader2,
  Paperclip,
  Trash2,
  Upload,
} from "lucide-react";
import clsx from "clsx";

import { useT } from "../i18n";
import { katalog } from "../i18n/lang";
import { Card, Chip, EmptyState, StatusBadge } from "../ui";
import { useToast } from "../ui/toast";
import { fragebogenApi, type AnlagenBefund, type Frage, type RunAnlage } from "./api";
import { abgelaufen, datum } from "./labels";

/** Farbe + Icon der Lage. Der TEXT steht im Katalog (`lageVorhanden` …) —
 *  dieselbe Trennung wie in `labels.ts`. */
const LAGE: Record<
  AnlagenBefund["lage"],
  { status: "success" | "warning" | "danger"; icon: JSX.Element }
> = {
  vorhanden: { status: "success", icon: <FileCheck2 className="w-4 h-4" /> },
  pruefen: { status: "warning", icon: <FileQuestion className="w-4 h-4" /> },
  fehlt: { status: "danger", icon: <FileX2 className="w-4 h-4" /> },
};

/** Die Beleg-Fragen eines Laufs: alle, an denen der Server einen Befund hat. */
export function anlagenFragen(fragen: Frage[]): Frage[] {
  return fragen.filter((f) => f.suggestion?.anlage?.quelle === "registry");
}

/** Die geltende Antwort einer Beleg-Frage: die freigegebene, sonst der Vorschlag.
 *  `ja_nein` (yes_no_text) hat Vorrang, weil es das reine Ja/Nein trägt. */
export function belegAntwort(f: Frage): string {
  return String(
    f.final_answer?.ja_nein ??
      f.final_answer?.answer ??
      f.suggestion?.ja_nein ??
      f.suggestion?.answer ??
      ""
  ).trim();
}

/** Wurde die Beleg-Frage mit „No"/„N/A" beantwortet? Solche Belege werden dem
 *  Versand nicht beigelegt und stehen in einer eigenen Sektion (WH 2026-08-21). */
export function belegAbgelehnt(f: Frage): boolean {
  return /^(no|nein|n\/a|na|not applicable|nicht zutreffend)\b/i.test(belegAntwort(f));
}

export default function AnlagenCheckliste({
  fragen,
  registryGroesse,
  produkt,
  fragebogenId,
  onAnlagenGeaendert,
}: {
  fragen: Frage[];
  registryGroesse: number;
  produkt: string | null;
  fragebogenId: string;
  /** Nach einem Upload/Löschen einer Lauf-Anlage — z. B. um Zähler neu zu laden. */
  onAnlagenGeaendert?: () => void;
}) {
  const t = useT().questionnaires;
  const zeilen = anlagenFragen(fragen);

  // „No"/„N/A"-Belege stehen getrennt darunter — sie werden nicht beigelegt und
  // sollen die Beizulegen-Liste nicht aufblähen (WH 2026-08-21).
  const abgelehnt = zeilen.filter(belegAbgelehnt);
  const aktiv = zeilen.filter((f) => !belegAbgelehnt(f));

  const lagen = aktiv.map((f) => f.suggestion!.anlage!.lage);
  const zaehler = {
    vorhanden: lagen.filter((l) => l === "vorhanden").length,
    pruefen: lagen.filter((l) => l === "pruefen").length,
    fehlt: lagen.filter((l) => l === "fehlt").length,
  };

  return (
    <div className="space-y-4">
      {zeilen.length === 0 ? (
        <Card padding="none">
          <EmptyState
            icon={<Paperclip className="w-6 h-6" />}
            title={t.keineAnlagenTitel}
            hint={t.keineAnlagenHinweis}
          />
        </Card>
      ) : (
        <div className="space-y-3">
          {aktiv.length > 0 && (
            <>
          <div className="flex items-center gap-2 flex-wrap text-sm">
            <span className="text-text-2">{t.belegFragen(aktiv.length)}</span>
            <StatusBadge status="success">{t.zaehlerVorhanden(zaehler.vorhanden)}</StatusBadge>
            <StatusBadge status="warning">{t.zaehlerPruefen(zaehler.pruefen)}</StatusBadge>
            <StatusBadge status="danger">{t.zaehlerFehlt(zaehler.fehlt)}</StatusBadge>
            <span className="text-xs text-text-3">
              {t.registryGefiltert(produkt ?? t.registryGlobal, registryGroesse)}
            </span>
          </div>
          <p className="text-xs text-text-3">{t.abgleichHinweis}</p>

          <div className="space-y-2">
            {aktiv.map((f) => (
              <AnlagenReihe key={f.id} frage={f} befund={f.suggestion!.anlage!} />
            ))}
          </div>
        </>
      )}

      {abgelehnt.length > 0 && (
        <div className="space-y-2 pt-2">
          <div className="flex items-center gap-2 border-t border-border pt-3 text-sm">
            <FileX2 className="w-4 h-4 text-text-3 shrink-0" />
            <span className="font-medium text-text-2">
              {t.anlagenAbgelehntTitel(abgelehnt.length)}
            </span>
          </div>
          <p className="text-xs text-text-3">{t.anlagenAbgelehntHinweis}</p>
          <div className="space-y-2 opacity-80">
            {abgelehnt.map((f) => (
              <AnlagenReihe
                key={f.id}
                frage={f}
                befund={f.suggestion!.anlage!}
                antwort={belegAntwort(f)}
              />
            ))}
          </div>
        </div>
      )}
        </div>
      )}
      <RunAnlagenBlock
        fragebogenId={fragebogenId}
        onGeaendert={onAnlagenGeaendert}
      />
    </div>
  );
}

/**
 * „Anlagen ergänzen" — fehlende Belege per Drag&Drop diesem Lauf beilegen
 * (WH 2026-08-21). Die Dateien liegen am Lauf (`q_questionnaire_attachment`),
 * erscheinen hier und wandern ins Download-Paket. Muster der Dropzone wie in
 * `Uebersicht.tsx`.
 */
function RunAnlagenBlock({
  fragebogenId,
  onGeaendert,
}: {
  fragebogenId: string;
  onGeaendert?: () => void;
}) {
  const t = useT().questionnaires;
  const toast = useToast();
  const [liste, setListe] = useState<RunAnlage[] | null>(null);
  const [over, setOver] = useState(false);
  const [busy, setBusy] = useState(false);

  const laden = useCallback(async () => {
    try {
      setListe(await fragebogenApi.runAnlagen(fragebogenId));
    } catch {
      setListe([]);
    }
  }, [fragebogenId]);

  useEffect(() => {
    laden();
  }, [laden]);

  const hochladen = async (datei: File) => {
    setBusy(true);
    try {
      await fragebogenApi.runAnlageHochladen(fragebogenId, datei);
      toast.ok(katalog().questionnaires.anlageHochgeladen(datei.name));
      await laden();
      onGeaendert?.();
    } catch (e) {
      toast.err(katalog().questionnaires.anlageUploadFehlgeschlagen, (e as Error).message);
    } finally {
      setBusy(false);
      setOver(false);
    }
  };

  const loeschen = async (a: RunAnlage) => {
    try {
      await fragebogenApi.runAnlageLoeschen(fragebogenId, a.id);
      setListe((s) => (s ?? []).filter((x) => x.id !== a.id));
      onGeaendert?.();
    } catch (e) {
      toast.err(katalog().questionnaires.anlageLoeschenFehlgeschlagen, (e as Error).message);
    }
  };

  return (
    <div className="space-y-2 border-t border-border pt-3">
      <p className="text-sm font-medium text-text-2">{t.eigeneAnlagenTitel}</p>
      <p className="text-xs text-text-3">{t.eigeneAnlagenHinweis}</p>

      <label
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          const f = e.dataTransfer.files?.[0];
          if (f) hochladen(f);
        }}
        className={clsx(
          "flex cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-5 text-center text-sm transition-colors",
          over
            ? "border-brand-600 bg-brand-50/50 dark:bg-brand-700/10"
            : "border-border-strong hover:border-brand-500"
        )}
      >
        {busy ? (
          <Loader2 className="h-4 w-4 animate-spin text-brand-500" />
        ) : (
          <Upload className="h-4 w-4 text-brand-500" />
        )}
        <span className="text-text-2">{busy ? t.anlageWirdHochgeladen : t.anlageAblegen}</span>
        <input
          type="file"
          aria-label={t.anlageAblegen}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) hochladen(f);
            e.target.value = "";
          }}
          className="sr-only"
        />
      </label>

      {liste && liste.length > 0 && (
        <ul className="space-y-1">
          {liste.map((a) => (
            <li
              key={a.id}
              data-run-anlage
              className="flex items-center justify-between gap-2 rounded-md border border-border bg-surface px-2.5 py-1.5 text-sm"
            >
              <span className="inline-flex min-w-0 items-center gap-2">
                <Paperclip className="h-3.5 w-3.5 shrink-0 text-text-3" />
                <span className="truncate" title={a.filename}>
                  {a.filename}
                </span>
              </span>
              <button
                type="button"
                onClick={() => loeschen(a)}
                title={t.anlageEntfernen}
                aria-label={t.anlageEntfernen}
                className="rounded-md p-1 text-text-2 hover:bg-danger-soft hover:text-danger"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function AnlagenReihe({
  frage,
  befund,
  antwort,
}: {
  frage: Frage;
  befund: AnlagenBefund;
  /** Nur in der „Abgelehnt/N/A"-Sektion gesetzt: die geltende Antwort. */
  antwort?: string;
}) {
  const t = useT().questionnaires;
  const lage = LAGE[befund.lage];
  const lageLabel =
    befund.lage === "vorhanden"
      ? t.lageVorhanden
      : befund.lage === "pruefen"
        ? t.lagePruefen
        : t.lageFehlt;
  // Beizulegende Belege: die von der Antwort benannten (z. B. „13485 and
  // 14001") oder — für ältere Läufe/Regelfall — der eine beste (WH #23).
  const belege =
    befund.beizulegen && befund.beizulegen.length > 0
      ? befund.beizulegen
      : befund.bester
        ? [befund.bester]
        : [];
  const istNichtZutreffend = /^(n\/a|na|not applicable|nicht zutreffend)\b/i.test(
    antwort ?? ""
  );
  return (
    <Card padding="sm" data-anlagen-lage={befund.lage} data-anlagen-antwort={antwort}>
      <div className="flex items-start gap-3">
        <span
          className={clsx(
            "mt-0.5 shrink-0",
            befund.lage === "vorhanden"
              ? "text-success"
              : befund.lage === "pruefen"
                ? "text-warning"
                : "text-danger"
          )}
        >
          {lage.icon}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <span className="text-sm font-medium">{frage.question_text}</span>
            <div className="flex items-center gap-1.5">
              {antwort && (
                <StatusBadge status={istNichtZutreffend ? "neutral" : "danger"}>
                  {antwort}
                </StatusBadge>
              )}
              <StatusBadge status={lage.status}>{lageLabel}</StatusBadge>
            </div>
          </div>

          {belege.length > 0 ? (
            <div className="mt-1.5 space-y-2 text-sm">
              {belege.length > 1 && (
                <p className="text-xs font-medium text-text-2">
                  {t.belegeBeilegen(belege.length)}
                </p>
              )}
              {belege.map((b) => (
                <BelegKarte key={b.id} beleg={b} mehrfach={belege.length > 1} />
              ))}
              {befund.vertraulich && (
                <div className="text-xs rounded-md px-2 py-1.5 bg-danger-soft text-danger flex items-start gap-1.5">
                  <AlertTriangle className="w-3.5 h-3.5 mt-px shrink-0" />
                  <span>
                    <span className="font-semibold">{t.vertraulich}</span>{" "}
                    {t.vertraulichHinweis}
                  </span>
                </div>
              )}
            </div>
          ) : (
            <p className="mt-1.5 text-sm text-text-2">{t.keinBelegHinweis}</p>
          )}
        </div>
      </div>
    </Card>
  );
}

/** Ein einzelner beizulegender Beleg — Titel, Stand, Gültigkeit, SF-Link.
 *  Eine Frage kann mehrere davon nach sich ziehen (WH 2026-08-21, #23). */
function BelegKarte({
  beleg,
  mehrfach,
}: {
  beleg: NonNullable<AnlagenBefund["bester"]>;
  /** Bei mehreren Belegen je Frage: leicht abgesetzt, damit sie als Liste lesbar sind. */
  mehrfach: boolean;
}) {
  const t = useT().questionnaires;
  return (
    <div
      data-beleg={beleg.seed_id}
      className={clsx(mehrfach && "border-l-2 border-border pl-2.5")}
    >
      <span className="text-text">{beleg.title}</span>
      <div className="mt-1 flex items-center gap-1.5 flex-wrap text-[0.72rem] text-text-3">
        <Chip>{beleg.type}</Chip>
        {beleg.latest ? (
          <Chip title={t.aktuellTooltip}>{t.aktuell}</Chip>
        ) : (
          <Chip title={t.veraltetTooltip}>{t.veraltet}</Chip>
        )}
        {beleg.valid_until && (
          <span
            className={clsx(abgelaufen(beleg.valid_until) && "text-danger font-medium")}
          >
            {t.gueltigBis(datum(beleg.valid_until))}
            {/* Ein abgelaufenes Zertifikat als „Beleg vorhanden" zu zeigen,
                wäre die gefährlichere Hälfte der Wahrheit. */}
            {abgelaufen(beleg.valid_until) && t.abgelaufen}
          </span>
        )}
        <span>{t.uebereinstimmung(beleg.score.toFixed(2))}</span>
      </div>
      {/* Read-only-Link zur Beleg-Datei in Salesforce (Mig 0104). Öffnet das
          Dokument dort, wo es liegt — kein Schreibpfad. */}
      {beleg.sf_url && (
        <a
          href={beleg.sf_url}
          target="_blank"
          rel="noopener noreferrer"
          data-sf-link
          className="mt-1 inline-flex items-center gap-1 text-[0.72rem] text-brand-600 hover:underline"
        >
          <ExternalLink className="w-3 h-3 shrink-0" />
          {t.inSalesforceOeffnen}
        </a>
      )}
    </div>
  );
}
